// Gate for the "never publish a raw camera frame" privacy path (OSPROD-197).
//
// When ON, mediaManager attaches the background/zoom processor to a LIVE but
// UNPUBLISHED camera track and publishes only the processed canvas (single-layer)
// — peers never receive the raw (real-surroundings) frame, and camera-resolution
// simulcast is irrelevant (canvas is single-layer). When OFF, the camera path is
// byte-for-byte the legacy behaviour (publish raw → attach processor after, with
// a ~1-2s raw window at camera-on).
//
// DEFAULT ON since 2026-06-23 — verified live on prod (real call + auto-face-center
// + 2nd participant; video not frozen for peers). Was default OFF during rollout
// because `main` is staging and ANY `main → production` MR sweeps main into prod.
// Per-device kill-switch: localStorage 'rx2go.videoPrivacy' = 'off' (or build with
// VITE_VIDEO_PRIVACY=off). Global rollback: flip this default back + redeploy.
//
// Override order (first match wins):
//   1. in-memory session override (runtime / tests)        — not persisted
//   2. localStorage 'rx2go.videoPrivacy' = 'on' | 'off'    (per-device toggle / kill-switch)
//   3. import.meta.env.VITE_VIDEO_PRIVACY = 'on' | 'off'   (build-time)
//   4. true                                                (default — privacy ON)

let sessionOverride: boolean | null = null

/** Force the flag for the rest of this page session (not persisted). */
export function setVideoPrivacySessionOverride(on: boolean | null): void {
  sessionOverride = on
}

export function videoPrivacyEnabled(): boolean {
  if (sessionOverride !== null) return sessionOverride
  try {
    const ls = typeof localStorage !== 'undefined' ? localStorage.getItem('rx2go.videoPrivacy') : null
    if (ls === 'on') return true
    if (ls === 'off') return false
  } catch { /* localStorage unavailable (SSR / privacy mode) — fall through */ }
  const env = (import.meta as any)?.env?.VITE_VIDEO_PRIVACY
  if (env === 'on') return true
  if (env === 'off') return false
  return true
}
